import AutoCompleteInput from "@/components/inputs/AutoCompleteInput";
import { sortArrayByKey } from "@/lib/utils/sorting";
import dayjs from "dayjs";
import { parseDate } from "@internationalized/date";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import { Button } from "@heroui/react";
import DateInput from "@/components/inputs/DateInput";
import TextInput from "@/components/inputs/TextInput";
import SelectButtonInput from "@/components/inputs/SelectButtonInput";
import { useFormContext } from "react-hook-form";
import { CropConfiguration } from "@/types/cropConfiguration";
import { useTranslation } from "react-i18next";

function PlantingCropDetailsForm({
    crops,
    selectedCrop,
}: {
    crops: { id: number; description: string }[];
    selectedCrop?: CropConfiguration;
}) {
    const { t } = useTranslation(["operations", "messages"]);
    const { setValue } = useFormContext();

    return (
        <>
            <AutoCompleteInput
                name="crop_configuration"
                isRequired
                options={sortArrayByKey(crops, "description")}
                labelPlacement="outside"
                placeholder="Select Crop"
                label={t("crop")}
                size="sm"
            />
            <div className="flex flex-row gap-2 items-end ">
                <DateInput
                    name="start_date"
                    label={t("plantingDate")}
                    variant="flat"
                    isRequired
                    size="sm"
                    hideErrorMessage
                    labelPlacement="outside"
                    showMonthAndYearPickers
                    placeholderValue={parseDate(dayjs().format("YYYY-MM-DD"))}
                />
                <div>
                    <Button
                        size="sm"
                        onPress={() => {
                            setValue(
                                "start_date",
                                dayjs(
                                    selectedCrop?.default_planting_date,
                                ).toDate(),
                            );
                        }}
                        color="primary"
                        isDisabled={!selectedCrop}
                        startContent={<FontAwesomeIcon icon={faCalendar} />}
                    >
                        {t("default")}
                    </Button>
                </div>
            </div>
            <div className="grid grid-flow-col gap-2 items-end self-start ">
                <TextInput
                    name="hybrid"
                    label={t("hybrid")}
                    labelPlacement="outside"
                    size="sm"
                    placeholder={t("hybrid")}
                />
                {selectedCrop?.planting_form_fields.includes("crm") && (
                    <TextInput
                        type="number"
                        min={80}
                        max={120}
                        name="crm"
                        label="CRM"
                        placeholder="100"
                        labelPlacement="outside"
                        size="sm"
                    />
                )}
                {selectedCrop?.planting_form_fields.includes(
                    "maturity_level",
                ) && (
                    <SelectButtonInput
                        name="maturity_level"
                        label={t("maturityLevel")}
                        size="sm"
                        orientation="horizontal"
                        classNames={{
                            base: "flex-1 w-full",
                            label: "text-xs",
                        }}
                        radioProps={{
                            size: "sm",
                        }}
                        options={[
                            { description: t("early"), id: "early" },
                            { description: t("medium"), id: "medium" },
                            { description: t("late"), id: "late" },
                        ]}
                    />
                )}
                {selectedCrop?.planting_form_fields.includes("rice_type") && (
                    <SelectButtonInput
                        name="rice_type"
                        label="Rice Type"
                        size="sm"
                        orientation="horizontal"
                        classNames={{
                            base: "flex-1 w-full",
                            label: "text-xs",
                        }}
                        radioProps={{
                            size: "sm",
                        }}
                        options={[
                            { description: "American", id: "american" },
                            { description: "Japanese", id: "japanese" },
                        ]}
                    />
                )}
                {selectedCrop?.planting_form_fields.includes(
                    "sugarcane_type",
                ) && (
                    <SelectButtonInput
                        name="sugarcane_type"
                        label="Sugarcane Type"
                        size="sm"
                        orientation="horizontal"
                        classNames={{
                            base: "flex-1 w-full",
                            label: "text-xs",
                        }}
                        radioProps={{
                            size: "sm",
                        }}
                        options={[
                            {
                                description: "High Fiber",
                                id: "high_fiber",
                            },
                            {
                                description: "High Sucrose",
                                id: "high_sucrose",
                            },
                        ]}
                    />
                )}
                {selectedCrop?.planting_form_fields.includes(
                    "soybean_maturity_group",
                ) && (
                    <TextInput
                        type="number"
                        min={1}
                        max={11}
                        decimalPlaces={1}
                        name="soybean_maturity_group"
                        label={t("maturityLevel")}
                        placeholder="1"
                        labelPlacement="outside"
                        size="sm"
                    />
                )}
            </div>
        </>
    );
}

export default PlantingCropDetailsForm;
